import React, { useState, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import AnimatedArcs from '../../common/AnimatedArcs';
import {
	ICareersFilterProps,
	memoizeOptionsForReactSelect,
} from '../CareerCardGroup';
import qs from 'qs';
import Select from '../../common/ReactSelect';

export interface ICareersHeroAreaI18n {
	searchLabel: string;
	searchPlaceholder: string;
	buttonLabel: string;
}

export interface ICareersHeroAreaProps extends IBaseElementProps {
	/**
	 * Tagline text before the heading.
	 * @noUI
	 * @control richtext
	 */
	tagline?: string;
	/**
	 * Heading text - bold text will have color gradient applied
	 * @control richtext
	 */
	heading?: string;
	/**
	 * Line Animation Delay in Milliseconds
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels
	 */
	lineAnimationOffset?: number;
	/**
	 * @noUI
	 */
	searchURL?: string;
	/**
	 * @noUI
	 */
	filters?: ICareersFilterProps[];
	/**
	 * @noUI
	 */
	i18n: ICareersHeroAreaI18n;
}

const defaultFilters: ICareersFilterProps[] = [
	{
		label: 'Department',
		taxonomy: 'department',
		options: [
			{
				label: 'Department',
				value: '',
			},
			{
				label: 'Health',
				value: 'health',
			},
			{
				label: 'Information Technology',
				value: 'information-technology',
			},
		],
	},
	{
		label: 'Location',
		taxonomy: 'location',
		options: [
			{
				label: 'Location',
				value: '',
			},
			{
				label: 'Saskatoon, SK',
				value: 'saskatoon-sk',
			},
			{
				label: 'Houston, TX',
				value: 'houston-tx',
			},
		],
	},
];

/**
 * Careers Hero Area
 *
 * Note: There are a few properties in here that will be filled in automatically on the server when this block is displayed, namely
 * the tagline and the form labels and placeholders.
 *
 * @block
 * @icon superhero
 *
 */
const CareersHeroArea: React.FC<ICareersHeroAreaProps> = ({
	id,
	className,
	classNames = [],
	tagline = '<strong>210+</strong> jobs in <strong>81+</strong> locations',
	heading,
	lineAnimationDelay = 1000,
	lineAnimationOffset = 150,
	searchURL = '',
	filters = defaultFilters,
	i18n = {
		searchLabel: 'Search',
		searchPlaceholder: 'Search jobs and keywords',
		buttonLabel: 'Go',
	},
}) => {
	const [state, setState] = useState<{ [key: string]: string }>({});
	const updateState = (key: string, value: string) => {
		const newState = {
			...state,
			[key]: value,
		};
		if (!value) {
			delete newState[key];
		}
		setState(newState);
	};
	const [filterOptions, searchConfig] = useMemo(
		() => memoizeOptionsForReactSelect(filters),
		[filters],
	);
	const onSubmit = (e) => {
		if (window) {
			e.preventDefault();
			window.location.href = `${searchURL}#!${qs.stringify(state)}`;
		}
	};

	return (
		<div
			id={id}
			className={
				className || cn('calian-careers-hero-area', ...classNames)
			}
		>
			<div className="calian-careers-hero-area__container container-fluid">
				<div className="row">
					<div className="col-12">
						<div className="calian-careers-hero-area__content">
							{tagline && (
								<p className="calian-careers-hero-area__tagline">
									<span
										dangerouslySetInnerHTML={{
											__html: tagline,
										}}
									/>
								</p>
							)}
							{heading && (
								<h1 className="calian-careers-hero-area__heading">
									<span
										dangerouslySetInnerHTML={{
											__html: heading,
										}}
									/>
								</h1>
							)}
						</div>
					</div>
					<div className="col-12">
						<form
							className="calian-careers-hero-area__filter-bar"
							action={searchURL}
							onSubmit={onSubmit}
						>
							<div className="calian-careers-hero-area__form d-flex">
								<div className="calian-careers-hero-area__form--search-input">
									<label
										htmlFor="search"
										className="visually-hidden"
									>
										{i18n.searchLabel}
									</label>
									<input
										type="text"
										className="form-control"
										id="search"
										placeholder={i18n.searchPlaceholder}
										value={state.search || ''}
										onChange={(e) =>
											updateState(
												'search',
												e.target.value,
											)
										}
									/>
								</div>
								{filters.map((filter) => (
									<div
										key={filter.taxonomy}
										className="calian-careers-hero-area__form--select"
									>
										<label
											htmlFor={filter.taxonomy}
											className="visually-hidden"
										>
											{filter.label}
										</label>
										<Select
											className="form-select react-form-select"
											classNamePrefix="react-select"
											isSearchable
											isClearable
											id={filter.taxonomy}
											placeholder={filter.label}
											options={Object.values(
												filterOptions[filter.taxonomy],
											)}
											onChange={(o: any) => {
												updateState(
													filter.taxonomy,
													o?.value || '',
												);
											}}
											aria-label={filter.label}
											value={
												filterOptions[filter.taxonomy][
													(state || {})[
														filter.taxonomy
													] || ''
												]
											}
											filterOption={
												searchConfig as (
													option: any,
													rawInput: string,
												) => boolean
											}
										/>
									</div>
								))}
								<div className="calian-careers-hero-area__form--submit">
									<button
										type="submit"
										className="btn btn-primary"
									>
										{i18n.buttonLabel}
									</button>
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
			<AnimatedArcs
				arcsStyle={'Hero - Three Lines'}
				arcsPosition="Right"
				arcOneColor="var(--bs-white)"
				arcOneStrokeWidth={4}
				arcTwoColor="var(--bs-light)"
				arcTwoStrokeWidth={3}
				arcThreeColor="var(--bs-white)"
				arcThreeStrokeWidth={2}
				animationDelay={lineAnimationDelay}
				animationOffset={lineAnimationOffset}
			/>
		</div>
	);
};

export default CareersHeroArea;
